import { EVENT } from "@/constants/event";
import useAppStore from "@/context/store";
import { safeParse } from "@/utils/safeParse";
import { useMemoizedFn, useMount } from "ahooks";
import Cookies from "js-cookie";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { create } from "zustand";

export const SIDE_NAV_COLLAPSED_KEY = "side_nav_collapsed";

export function getSideNavCollapsedState(req, isPageCache = false) {
  if (isPageCache) {
    // 如果页面缓存，则默认收起
    return true;
  }

  if (typeof window === "undefined") {
    if (req) {
      // 服务端渲染初始化时使用，从cookie中获取
      return safeParse(req?.cookies?.[SIDE_NAV_COLLAPSED_KEY], () => true);
    } else {
      // 服务端渲染过程使用
      return useAppStore.getState().isCollapsed;
    }
  }
  // 客户端
  return safeParse(Cookies.get(SIDE_NAV_COLLAPSED_KEY), () => true);
}

// 创建一个共享的 store
export const useSideNavStore = create<{
  isCollapsed?: boolean;
  setIsCollapsed: (value: boolean) => void;
}>((set) => ({
  setIsCollapsed: (value) => {
    Cookies.set(SIDE_NAV_COLLAPSED_KEY, value.toString(), { expires: 365 }); // 1年过期
    set({ isCollapsed: value });
  },
}));

export default function useSideNavCollapse() {
  const isPageCache = useAppStore((state) => state.isPageCache);

  const defaultValue = getSideNavCollapsedState(null, isPageCache);
  const { isCollapsed = defaultValue, setIsCollapsed } = useSideNavStore();

  // 初始化状态
  useMount(() => {
    const cookieValue = safeParse(
      Cookies.get(SIDE_NAV_COLLAPSED_KEY),
      () => true
    );
    useSideNavStore.setState({ isCollapsed: cookieValue });
  }); // 只在组件挂载时执行一次

  const setSideNavCollapsed = useMemoizedFn((value) => {
    setIsCollapsed(value);

    // 触发事件，通知瀑布流重排（同步执行，优化更快）
    window.dispatchEvent(
      new CustomEvent(EVENT.SIDBAR_COLLAPSED, { detail: value })
    );
  });

  return [isCollapsed, setSideNavCollapsed];
}

export function useSideNavVisible() {
  const { pathname } = useRouter();
  return useMemo(() => {
    return ![
      "/activities", // 大赛
      "/activities/start", // 大赛发起
      "/albums/[urlname]", // 合辑
      "/promotions/pintuan/[code]", // 拼团活动
      "/settings", // 个人设置
      "/me/[page]", // 个人设置
      "/me/[page]/[tab]", // 个人设置
      "/about",
      "/about/goodies",
      "/apps",
      "/about/contact",
      "/about/disclaimer",
      "/vip/consult",
      "/ideas/[urlname]",
      // 全屏详情页现在也显示左侧导航，所以移除了 /pins/[pinId]
    ].includes(pathname);
  }, [pathname]);
}
